import { useCallback, useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'

import * as cartRepository from 'scripts/api/cart'

import { dataLayer } from 'reactSite/Cart/services'

import { Quantity } from 'checkout/components/Quantity'

const Container = ({
  id,
  productId,
  name,
  price,
  brand,
  categoryTree,
  shopName,
  type,
  quantity,
  packaging,
  packagingCount,
  skuId,
  storeId,
  updateProduct,
  onError,
  onSuccess,
  fromCart,
  regionId
}) => {
  const [currentPackaging, setPackaging] = useState(packagingCount)
  const [productMaxStock, setProductMaxStock] = useState(Infinity)
  const [isReadyToUpdate, setIsReadyToUpdate] = useState(false)
  const [isProductRemoved, setIsProductRemoved] = useState(undefined)
  const [deltaValue, setDelta] = useState(0)

  const useFormatQuantity = useMemo(() => {
    return Math.round((packaging * currentPackaging + Number.EPSILON) * 100) / 100
  }, [currentPackaging, packaging])

  const handleInputChange = useCallback((value) => {
    try {
      if (value && value > 0) {
        const delta = currentPackaging > value ? currentPackaging - value : value - currentPackaging
        setIsReadyToUpdate(true)
        setDelta(delta)
        setPackaging(value)
      }
    } catch (e) {
      setPackaging(p => p)
    }
  }, [currentPackaging])

  const updateQuantity = useCallback(({ value, isDecreasing = false }) => {
    const newPackaging = (parseInt(currentPackaging * 1000) + parseInt(value * 1000)) / 1000
    setDelta(d => d + value)

    if (isDecreasing && newPackaging > productMaxStock) {
      setPackaging(newPackaging)
    }

    if (newPackaging > 0 && newPackaging <= productMaxStock) {
      setPackaging(newPackaging)
    }

    setIsReadyToUpdate(true)
    setIsProductRemoved(isDecreasing)
  }, [currentPackaging, productMaxStock])

  const dataLayerTrigger = useCallback(() => {
    const quantityChanged = deltaValue > 0 ? deltaValue : deltaValue * -1

    if (isProductRemoved) {
      dataLayer.removeProduct({
        brand,
        categoryTree,
        price,
        productId,
        name,
        quantity: quantityChanged,
        regionId
      })
    } else {
      dataLayer.addProduct({
        brand,
        categoryTree,
        id: productId,
        name,
        price,
        type,
        quantity: quantityChanged,
        shopName,
        regionId
      })
    }
  }, [
    brand,
    categoryTree,
    productId,
    name,
    price,
    type,
    shopName,
    regionId,
    isProductRemoved,
    deltaValue
  ])

  useEffect(() => {
    setIsReadyToUpdate(true)

    return () => {
      setIsReadyToUpdate(false)
    }
  }, [])

  useEffect(() => {
    let updateCart

    if (isReadyToUpdate && packagingCount !== currentPackaging) {
      updateCart = setTimeout(async () => {
        await cartRepository.putProductsInCart({
          type,
          storeId: storeId || null,
          products: [
            {
              id,
              skuId: skuId || null,
              quantity: currentPackaging
            }
          ]
        }).then(response => {
          const { data } = response

          dataLayerTrigger()

          updateProduct({ currentId: id, ...data })
          onSuccess({ data, currentPackaging, productMaxStock })
        }).catch(({ response: { data } }) => {
          const maxStock = data?.stock.find(product => product.id === id)?.max
          if (maxStock) {
            setPackaging(maxStock)
            setProductMaxStock(maxStock)
          }

          if (data?.errors) {
            onError(data)
          }
        }).finally(() => {
          setDelta(0)
        })
      }, 500)
    }

    return () => {
      clearTimeout(updateCart)
    }
  }, [
    currentPackaging,
    type,
    onError,
    onSuccess,
    productMaxStock,
    packagingCount,
    skuId,
    storeId,
    updateProduct,
    dataLayerTrigger,
    id,
    isReadyToUpdate
  ])

  useEffect(() => {
    setIsReadyToUpdate(false)
    setPackaging(packagingCount)
  }, [packagingCount])

  return (
    <Quantity
      fromCart={fromCart}
      useFormatQuantity={useFormatQuantity}
      packaging={packaging}
      updateQuantity={updateQuantity}
      handleInputChange={handleInputChange}
      packagingCount={packagingCount}
      productMaxStock={productMaxStock}
    />
  )
}

Container.propTypes = {
  id: PropTypes.string,
  quantity: PropTypes.number,
  packaging: PropTypes.number,
  skuId: PropTypes.string,
  storeId: PropTypes.string,
  type: PropTypes.string,
  updateProduct: PropTypes.func
}

Container.defaultProps = {
  onError: () => {},
  onSuccess: () => {}
}

export default Container
